/**
 * DisconnectedPanel — global closed-state screen.
 *
 * Rendered from App.tsx whenever `useConnection().kind === "closed"`. Replaces
 * Lobby/Table entirely so a terminal disconnect (BAD_CODE, ROOM_FULL,
 * RECLAIM_FAILED, GAME_IN_PROGRESS, or a give-up after exhausting reconnect
 * attempts) is never a dead end mid-play. Fixes Phase-3 UX-review F#7 (Table
 * had no closed-state path; only Lobby did).
 *
 * Buttons (default disconnect):
 *   - Rejoin room (v1.4.1 Phase 21.1): re-opens the socket with the
 *     persisted reclaim token + name so a transient disconnect doesn't
 *     force the user back to RoomGate. `actions.joinRoom` calls
 *     `socket.connect` which reads `loadReclaimToken(code)` and sends a
 *     `{ t: "join", code, name, reclaimToken }` on open — the server
 *     rebinds the seat without losing the in-progress game.
 *   - Back: original Phase-3 behaviour. Tears down + reloads, taking the
 *     user back to RoomGate to enter a new room.
 *
 * PROTOCOL_MISMATCH variant (v1.4.1 Phase 21.2):
 *   When `errorCode === "PROTOCOL_MISMATCH"` (set by the socket when the
 *   server's handshake check rejects our `protocolVersion`), Rejoin would
 *   immediately fail the same way against the same stale bundle. Instead
 *   we show a typed "App is out of date — refresh" message and a primary
 *   "Refresh now" button that hard-reloads the page so Cloudflare Pages
 *   serves the new bundle.
 */
import { actions } from "../wire/index.js";
import { useDisplayName } from "../state/hooks.js";

function reloadPage(): void {
  if (typeof window !== "undefined") {
    try {
      window.location.reload();
    } catch {
      /* jsdom / SSR */
    }
  }
}

export function DisconnectedPanel({
  reason,
  code,
  errorCode,
}: {
  reason: string;
  code: string;
  errorCode?: string;
}): JSX.Element {
  const name = useDisplayName();

  // v1.4.1 Phase 21.2: PROTOCOL_MISMATCH variant. Reconnecting would fail
  // identically against the same stale client bundle; only a full page
  // reload (which picks up the new bundle from Cloudflare Pages) can
  // recover. Show a typed message + primary Refresh button.
  if (errorCode === "PROTOCOL_MISMATCH") {
    return (
      <main
        className="lobby lobby-disconnected"
        aria-labelledby="lobby-disconnected-title"
      >
        <h1 id="lobby-disconnected-title" className="lobby-title">
          Hookum
        </h1>
        <p className="lobby-disconnected-msg">
          App is out of date — refresh to keep playing.
        </p>
        <div className="lobby-disconnected-actions">
          <button
            type="button"
            className="lobby-rejoin"
            onClick={() => {
              reloadPage();
            }}
          >
            Refresh now
          </button>
        </div>
      </main>
    );
  }

  // The Rejoin path only works when we have a room code AND a name to
  // rejoin as. Without a name the server would reject (zod validation on
  // displayName); fall back to Back in that case.
  const canRejoin = Boolean(code && name);
  return (
    <main
      className="lobby lobby-disconnected"
      aria-labelledby="lobby-disconnected-title"
    >
      <h1 id="lobby-disconnected-title" className="lobby-title">
        Hookum
      </h1>
      <p className="lobby-disconnected-msg">
        Disconnected{code ? ` from room ${code}` : ""}
        {reason ? ` — ${reason}.` : "."}
      </p>
      <div className="lobby-disconnected-actions">
        {canRejoin && (
          <button
            type="button"
            className="lobby-rejoin"
            onClick={() => {
              // Re-open the socket. `actions.joinRoom` → `socket.connect`
              // → `loadReclaimToken(code)` → `{ t: "join", reclaimToken }`
              // on socket open. The server rebinds the seat by token.
              actions.joinRoom(code, name);
            }}
          >
            Rejoin room
          </button>
        )}
        <button
          type="button"
          className="lobby-leave"
          onClick={() => {
            actions.disconnect();
            reloadPage();
          }}
        >
          Back
        </button>
      </div>
    </main>
  );
}
